import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { validarFormulario } from "../utils/validators";
import { formatearFecha, formatearMoneda } from "../utils/formatters";
import { createPresupuesto, updatePresupuesto, deletePresupuesto, aprobarPresupuesto as apiAprobar } from "../services/dataService";
import jsPDF from "jspdf";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import SectionHeader from "../components/ui/SectionHeader";
import Table from "../components/ui/Table";

export default function ModuloPresupuestos({ 
  presupuestos = [], setPresupuestos, tickets = [], setTickets, equipos = [], setEquipos, repuestos = [], registrarAuditoria, onNotify, activeTab
}) {
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === "Administrador";

  const [form, setForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [showRepuestosSelector, setShowRepuestosSelector] = useState(false);
  
  const getNextNumero = () => presupuestos.length === 0 ? 1 : Math.max(...presupuestos.map(p => p.numero || 0)) + 1;

  const ticketDelPresupuestoEntregado = (presupuestoId) => {
    const p = presupuestos.find(p => p.id === presupuestoId);
    if (!p?.ticketId) return false;
    const t = tickets.find(t => String(t.id) === String(p.ticketId));
    return t?.estado === "Entregado";
  };

  const [formData, setFormData] = useState({
    ticketId: "", 
    numero: getNextNumero(), 
    cliente: "", 
    domicilio: "",
    fecha: new Date().toISOString().slice(0, 10), 
    validez: "15",
    forma_pago: "Transferencia bancaria", 
    plazo_entrega: "a confirmar",
    items: [{ id: Date.now(), descripcion: "", cantidad: 1, precio: 0 }],
    observaciones: "Los valores están expresados en pesos.", 
    estado: "Pendiente",
    alicuota_iva: 21
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (setPresupuestos && presupuestos.length === 0) {
      const g = localStorage.getItem('cardiohard_presupuestos');
      if (g) setPresupuestos(JSON.parse(g));
    }
  }, [presupuestos.length, setPresupuestos]);

  useEffect(() => {
    if (presupuestos.length > 0) localStorage.setItem('cardiohard_presupuestos', JSON.stringify(presupuestos));
  }, [presupuestos]);

  useEffect(() => {
    if (activeTab === "presupuestos") {
      const data = sessionStorage.getItem('cardiohard_diag_to_quote');
      if (data) {
        try {
          const { equipo, diagnostico, items_sugeridos } = JSON.parse(data);
          setFormData(prev => ({
            ...prev,
            ticketId: diagnostico?.ticketId || prev.ticketId,
            cliente: equipo?.cliente || prev.cliente,
            domicilio: equipo?.domicilio || prev.domicilio,
            items: items_sugeridos?.length > 0 ? items_sugeridos : prev.items,
            observaciones: `Diagnóstico: ${diagnostico?.diagnostico?.slice(0, 80)}...`
          }));
          setForm(true);
          sessionStorage.removeItem('cardiohard_diag_to_quote');
          onNotify?.("📋 Datos del diagnóstico cargados. Completá los precios y detalles.", "info");
        } catch (e) {
          console.error("Error cargando diagnóstico", e);
        }
      }
    }
  }, [activeTab, onNotify]);

  const calcularSubtotal = () => formData.items.reduce((sum, item) => sum + (item.cantidad * item.precio), 0);
  const calcularIVA = () => calcularSubtotal() * (formData.alicuota_iva / 100);
  const calcularTotal = () => calcularSubtotal() + calcularIVA();

  const filtrados = presupuestos.filter(p => 
    p.cliente?.toLowerCase().includes(search.toLowerCase()) || 
    p.numero?.toString().includes(search)
  ).sort((a, b) => (b.numero || 0) - (a.numero || 0));

  const resetForm = () => {
    setFormData({ 
      ticketId: "", numero: getNextNumero(), cliente: "", domicilio: "", 
      fecha: new Date().toISOString().slice(0, 10), validez: "15", 
      forma_pago: "Transferencia bancaria", plazo_entrega: "a confirmar",
      items: [{ id: Date.now(), descripcion: "", cantidad: 1, precio: 0 }],
      observaciones: "Los valores están expresados en pesos.", 
      estado: "Pendiente",
      alicuota_iva: 21
    });
    setErrors({}); setEditing(null);
    setShowRepuestosSelector(false);
  };

  const agregarItem = () => setFormData(p => ({ ...p, items: [...p.items, { id: Date.now(), descripcion: "", cantidad: 1, precio: 0 }] }));
  const eliminarItem = (id) => { if (formData.items.length <= 1) return; setFormData(p => ({ ...p, items: p.items.filter(i => i.id !== id) })); };
  const actualizarItem = (id, campo, val) => setFormData(p => ({ ...p, items: p.items.map(i => i.id === id ? { ...i, [campo]: campo === 'cantidad' || campo === 'precio' ? parseFloat(val) || 0 : val } : i) }));

  // 🆕 FUNCIÓN PARA AGREGAR REPUESTO DESDE INVENTARIO
  const agregarRepuestoDesdeInventario = (repuesto) => {
    const existingItemIndex = formData.items.findIndex(i => i.descripcion.includes(repuesto.nombre));
    
    if (existingItemIndex >= 0) {
      // Si ya existe, aumentar cantidad
      const updatedItems = [...formData.items];
      updatedItems[existingItemIndex].cantidad += 1;
      setFormData(p => ({ ...p, items: updatedItems }));
      onNotify?.(`✅ ${repuesto.nombre} agregado (cantidad aumentada)`, "success");
    } else {
      // Si no existe, agregar nuevo item
      setFormData(p => ({
        ...p,
        items: [
          ...p.items.filter(i => i.descripcion), // Filtrar items vacíos
          {
            id: Date.now(),
            descripcion: `${repuesto.nombre}${repuesto.lote ? ` - Lote ${repuesto.lote}` : ''}`,
            cantidad: 1,
            precio: repuesto.precio_venta || 0
          }
        ]
      }));
      onNotify?.(`✅ ${repuesto.nombre} agregado al presupuesto`, "success");
    }
    setShowRepuestosSelector(false);
  };

  const handleSubmit = async () => {
    if (!esAdmin) { onNotify?.("Solo administradores pueden gestionar presupuestos", "error"); return; }
    const errores = validarFormulario(formData, ['cliente', 'fecha']);
    if (Object.keys(errores).length > 0 || formData.items.some(i => !i.descripcion || i.precio <= 0)) {
      setErrors(errores); onNotify?.("Completá campos obligatorios y verificá items", "warning"); return;
    }
    
    const data = { 
      ...formData, 
      subtotal: calcularSubtotal(), 
      iva: calcularIVA(), 
      total: calcularTotal(), 
      creado_por: usuario?.nombre, 
      creado_en: new Date().toISOString(),
      alicuota_iva: formData.alicuota_iva !== undefined ? formData.alicuota_iva : 21
    };
    
    try {
      if (editing) {
        await updatePresupuesto({ ...data, id: editing.id });
        setPresupuestos(p => p.map(x => x.id === editing.id ? { ...x, ...data } : x));
        registrarAuditoria?.(`Presupuesto #${data.numero} actualizado`, "Presupuestos");
        onNotify?.(`Presupuesto #${data.numero} actualizado`, "success");
      } else {
        if (data.ticketId && setTickets) {
          setTickets(prev => prev.map(t => {
            if (String(t.id) === String(data.ticketId)) {
              const estados = ["Recepción", "Diagnóstico", "Presupuesto", "Reparación", "Reparado", "Entregado"];
              const idxActual = estados.indexOf(t.estado);
              const idxPresupuesto = estados.indexOf("Presupuesto");
              if (idxActual !== -1 && idxActual < idxPresupuesto) {
                return { ...t, estado: "Presupuesto" };
              }
            }
            return t;
          }));
          onNotify?.(`🔁 Ticket #${data.ticketId} actualizado a "Presupuesto"`, "info");
        }
        const saved = await createPresupuesto(data);
        setPresupuestos(p => [...p, saved]);
        registrarAuditoria?.(`Presupuesto #${saved.numero} creado`, "Presupuestos");
        onNotify?.(`Presupuesto #${saved.numero} creado`, "success");
      }
    } catch (e) {
      onNotify?.(e.message, "error");
    }
    resetForm(); setForm(false);
  };

  const handleEdit = (p) => {
    if (!esAdmin) return;
    if (ticketDelPresupuestoEntregado(p.id)) {
      onNotify?.("⛔ No se puede modificar: el equipo ya fue entregado al cliente.", "error");
      return;
    }
    setFormData({ 
      ticketId: p.ticketId || "", 
      numero: p.numero, 
      cliente: p.cliente, 
      domicilio: p.domicilio || "", 
      fecha: p.fecha, 
      validez: p.validez || "15", 
      forma_pago: p.forma_pago || "Transferencia bancaria", 
      plazo_entrega: p.plazo_entrega || "a confirmar", 
      items: p.items?.length > 0 ? p.items : [{ id: Date.now(), descripcion: "", cantidad: 1, precio: 0 }], 
      observaciones: p.observaciones || "", 
      estado: p.estado || "Pendiente",
      alicuota_iva: p.alicuota_iva !== undefined ? p.alicuota_iva : 21
    });
    setEditing(p); setForm(true); setErrors({});
  };

  const aprobarPresupuesto = async (id) => {
    if (!esAdmin) return;
    
    if (ticketDelPresupuestoEntregado(id)) {
      onNotify?.("⛔ No se puede aprobar: el equipo ya fue entregado al cliente.", "error");
      return;
    }
    
    const presupuesto = presupuestos.find(p => p.id === id);
    if (!presupuesto) {
      onNotify?.("Presupuesto no encontrado", "error");
      return;
    }
   
    try {
      await apiAprobar(id);
      
      setPresupuestos(prev => prev.map(p => 
        p.id === id ? { 
          ...p, 
          estado: "Aprobado", 
          fecha_aprobacion: new Date().toISOString() 
        } : p
      ));
    
      if (presupuesto.ticketId && setTickets) {
        setTickets(prev => {
          const updated = prev.map(t => {
            if (String(t.id) === String(presupuesto.ticketId)) {
              if (t.equipoId && setEquipos) {
                setEquipos(prevEquipos => 
                  prevEquipos.map(e => 
                    String(e.id) === String(t.equipoId) 
                      ? { ...e, estado: "En reparación" } 
                      : e
                  )
                );
              }
              
              onNotify?.(`🔁 Ticket #${t.id} → Reparación`, "info");
              return { ...t, estado: "Reparación" };
            }
            return t;
          });
          return updated;
        });
      }
      
      registrarAuditoria?.(`Presupuesto #${presupuesto.numero} APROBADO`, "Presupuestos");
      onNotify?.("✅ Presupuesto APROBADO. Ticket y equipo pasaron a Reparación.", "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const handleDelete = async (id) => {
    if (!esAdmin) return;
    if (ticketDelPresupuestoEntregado(id)) {
      onNotify?.("⛔ No se puede eliminar: el equipo ya fue entregado al cliente.", "error");
      return;
    }
    if (window.confirm("¿Eliminar presupuesto?")) {
      try {
        await deletePresupuesto(id);
        setPresupuestos(p => p.filter(x => x.id !== id));
        registrarAuditoria?.(`Presupuesto #${id} eliminado`, "Presupuestos");
        onNotify?.("Eliminado", "info");
      } catch (e) {
        onNotify?.(e.message, "error");
      }
    }
  };

  const generarPDF = (p) => {
    const doc = new jsPDF();
    const alicuota = p.alicuota_iva !== undefined ? p.alicuota_iva : 21;
    
    doc.setFillColor(15, 76, 129); 
    doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(255, 255, 255); 
    doc.setFontSize(20); 
    doc.setFont("helvetica", "bold"); 
    doc.text("CARDIOHARD", 14, 15);
    doc.setFontSize(9); 
    doc.setFont("helvetica", "normal"); 
    doc.text("Av. Elcano 3985 - (1427) - Capital Federal", 14, 22);
    doc.text("Tel/Fax: (+5411)7505-9668 / (011)15-4022-2369", 14, 27);
    doc.text("info@cardiohard.com.ar | CUIT 30-71704987-6", 14, 32);
    
    doc.setTextColor(0, 0, 0); 
    doc.setFontSize(16); 
    doc.text("PRESUPUESTO", 140, 20);
    doc.setFontSize(10); 
    doc.text(`N° ${p.numero}`, 140, 28);
    
    doc.setFontSize(10); 
    doc.text(`Señor/es: ${p.cliente}`, 14, 50);
    doc.text(`Fecha: ${formatearFecha(p.fecha)}`, 140, 50);
    doc.text(`Domicilio: ${p.domicilio || "—"}`, 14, 57);
    doc.text(`Validez: ${p.validez} días`, 14, 64);
    
    let y = 80;
    doc.setFillColor(240, 240, 240); 
    doc.rect(14, y - 6, 182, 6, 'F');
    doc.setFont("helvetica", "bold"); 
    doc.text("Cant.", 18, y - 2); 
    doc.text("Descripción", 35, y - 2); 
    doc.text("P. Unit.", 130, y - 2); 
    doc.text("Total", 170, y - 2);
    doc.setFont("helvetica", "normal"); 
    y += 8;
    
    p.items?.forEach(i => {
      const descLines = doc.splitTextToSize(i.descripcion, 85);
      const itemHeight = Math.max(descLines.length * 5, 8);
      
      doc.text(i.cantidad.toString(), 18, y);
      doc.text(descLines, 35, y);
      doc.text(formatearMoneda(i.precio), 130, y);
      doc.text(formatearMoneda(i.cantidad * i.precio), 190, y, { align: "right" });
      y += itemHeight + 3;
    });
    
    y += 4; 
    doc.line(120, y, 196, y); 
    y += 9;
    
    doc.text("Sub Total:", 125, y);
    doc.text(formatearMoneda(p.subtotal), 190, y, { align: "right" }); 
    y += 9;
    
    doc.text(`Iva inc. ${alicuota}%:`, 125, y);
    doc.text(formatearMoneda(p.iva), 190, y, { align: "right" }); 
    y += 11;
    
    doc.setFillColor(200, 215, 230); 
    doc.rect(120, y - 4, 76, 9, 'F');
    doc.setFont("helvetica", "bold"); 
    doc.text("TOTAL:", 125, y + 1); 
    doc.text(formatearMoneda(p.total), 190, y + 1, { align: "right" });
    
    y += 22;
    doc.setFont("helvetica", "normal"); 
    doc.setFontSize(9);
    if (p.observaciones) { 
      doc.text("Observaciones:", 14, y); 
      y += 5; 
      const obsLines = doc.splitTextToSize(p.observaciones, 180);
      doc.text(obsLines, 14, y); 
      y += obsLines.length * 5 + 5;
    }
    doc.text(`Validez: ${p.validez} días | Entrega: ${p.plazo_entrega} | Pago: ${p.forma_pago}`, 14, y);
    y += 16; 
    doc.text("Sin otro particular lo saluda muy atte.,", 14, y);
    doc.setFont("helvetica", "bold"); 
    doc.text("CARDIOHARD SRL", 14, y + 6);
    
    doc.save(`Presupuesto_${p.numero}_${p.cliente.replace(/\s/g, '_')}.pdf`);
    onNotify?.("PDF descargado", "success");
  };

  const columnas = ["N°", "Cliente", "Fecha", "Total", "Estado", "Acciones"];
  const filas = filtrados.map(p => {
    const entregado = ticketDelPresupuestoEntregado(p.id);
    return [
      <strong key="n">#{p.numero}</strong>,
      <div key="c">{p.cliente}<br/><span className="text-xs text-muted">{p.domicilio}</span></div>,
      <span key="f" className="text-xs">{formatearFecha(p.fecha)}</span>,
      <strong key="t" style={{color:"#16a34a"}}>{formatearMoneda(p.total)}</strong>,
      <div key="e" className="flex gap-1 items-center">
        <Badge color={p.estado==="Aprobado"?"success":p.estado==="Rechazado"?"danger":"warning"}>{p.estado}</Badge>
        {entregado && <Badge color="muted">🔒 Bloqueado</Badge>}
      </div>,
      <div key="a" className="flex gap-1">
        {esAdmin && p.estado === "Pendiente" && !entregado && (
          <Btn small variant="success" onClick={()=>aprobarPresupuesto(p.id)}>✅ Aprobar</Btn>
        )}
        <Btn small variant="primary" onClick={()=>generarPDF(p)}>📄 PDF</Btn>
        {esAdmin && !entregado && <Btn small variant="default" onClick={()=>handleEdit(p)}>✏️</Btn>}
        {esAdmin && !entregado && <Btn small variant="danger" onClick={()=>handleDelete(p.id)}>🗑️</Btn>}
      </div>
    ];
  });

  return (
    <div>
      <SectionHeader title={`Presupuestos (${filtrados.length})`} subtitle="Gestión exclusiva de Administración" action={esAdmin && (
        <Btn variant="primary" onClick={()=>{setForm(f=>!f); if(form) resetForm();}}>{form ? "Cancelar" : "+ Nuevo Presupuesto"}</Btn>
      )} />

      {form && (
        <Card className="form-card">
          <p className="form-card__title">{editing ? `✏️ Editar #${formData.numero}` : `🆕 Nuevo Presupuesto #${formData.numero}`}</p>
          <div className="grid grid--2 gap-2 mb-2">
            <Input value={formData.cliente} onChange={e=>setFormData(p=>({...p, cliente:e.target.value}))} placeholder="Cliente *" error={errors.cliente} />
            <Input value={formData.domicilio} onChange={e=>setFormData(p=>({...p, domicilio:e.target.value}))} placeholder="Domicilio" />
            <Input value={formData.fecha} onChange={e=>setFormData(p=>({...p, fecha:e.target.value}))} type="date" />
            <Select value={formData.validez} onChange={e=>setFormData(p=>({...p, validez:e.target.value}))}>
              <option value="7">7 días</option><option value="15">15 días</option><option value="30">30 días</option>
            </Select>
            <Select value={formData.forma_pago} onChange={e=>setFormData(p=>({...p, forma_pago:e.target.value}))}>
              <option>Efectivo</option><option>Transferencia bancaria</option><option>Tarjeta</option>
            </Select>
            <Input value={formData.plazo_entrega} onChange={e=>setFormData(p=>({...p, plazo_entrega:e.target.value}))} placeholder="Plazo entrega" />
            
            <div>
              <label className="block text-sm font-medium mb-1">🧮 Alícuota de IVA</label>
              <select 
                value={formData.alicuota_iva}
                onChange={e => setFormData(p => ({ ...p, alicuota_iva: parseFloat(e.target.value) }))}
                className="input w-full"
              >
                <option value={0}>0% - Exento / No gravado</option>
                <option value={10.5}>10.5% - Reducido</option>
                <option value={21}>21% - General</option>
                <option value={27}>27% - Aumentado</option>
              </select>
            </div>
          </div>

          {/* Items con selector de inventario */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium">Items</label>
              <div className="flex gap-2">
                {repuestos.length > 0 && (
                  <Btn small variant="secondary" onClick={() => setShowRepuestosSelector(!showRepuestosSelector)}>
                    📦 Agregar desde Inventario
                  </Btn>
                )}
                <Btn small variant="default" onClick={agregarItem}>+ Item Manual</Btn>
              </div>
            </div>

            {/* 🆕 SELECTOR DE REPUESTOS DEL INVENTARIO */}
            {showRepuestosSelector && (
              <Card className="mb-3 bg-surface border-primary">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-medium text-sm">📦 Seleccionar repuesto del inventario</p>
                  <Btn small variant="default" onClick={() => setShowRepuestosSelector(false)}>✕</Btn>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {repuestos.map(rep => (
                    <div 
                      key={rep.id} 
                      className="flex justify-between items-center p-2 bg-white rounded cursor-pointer hover:bg-primary-light transition"
                      onClick={() => agregarRepuestoDesdeInventario(rep)}
                    >
                      <div>
                        <p className="font-medium text-sm">{rep.nombre}</p>
                        <p className="text-xs text-muted">Stock: {rep.stock} u. {rep.lote && `· Lote ${rep.lote}`}</p>
                      </div>
                      <Badge color={rep.stock <= rep.stock_critico ? "danger" : "success"}>
                        {rep.stock} disponibles
                      </Badge>
                    </div>
                  ))}
                  {repuestos.length === 0 && (
                    <p className="text-muted text-sm text-center py-2">No hay repuestos en inventario</p>
                  )}
                </div>
              </Card>
            )}

            {/* Lista de items */}
            {formData.items.map(i => (
              <div key={i.id} className="grid grid--4 gap-2 items-center mb-2 p-2 bg-surface rounded border">
                <Input value={i.descripcion} onChange={e=>actualizarItem(i.id,'descripcion',e.target.value)} placeholder="Descripción *" className="col-span-2" />
                <Input type="number" min="1" value={i.cantidad} onChange={e=>actualizarItem(i.id,'cantidad',e.target.value)} style={{width:"70px"}} />
                <div className="flex gap-1">
                  <Input type="number" min="0" step="0.01" value={i.precio} onChange={e=>actualizarItem(i.id,'precio',e.target.value)} style={{width:"100px"}} />
                  <Btn small variant="danger" onClick={()=>eliminarItem(i.id)}>×</Btn>
                </div>
              </div>
            ))}
          </div>

          {/* Totales */}
          <div className="flex gap-2 justify-between items-center bg-surface p-3 rounded mb-3">
            <div>
              <p>Subtotal: <b>{formatearMoneda(calcularSubtotal())}</b></p>
              <p>IVA ({formData.alicuota_iva}%): <b>{formatearMoneda(calcularIVA())}</b></p>
            </div>
            <p className="text-xl font-bold text-primary">TOTAL: {formatearMoneda(calcularTotal())}</p>
          </div>

          <textarea value={formData.observaciones} onChange={e=>setFormData(p=>({...p, observaciones:e.target.value}))} className="input w-full mb-3" rows="2" placeholder="Observaciones" />
          
          <div className="flex gap-2">
            <Btn variant="primary" onClick={handleSubmit}>{editing?"Actualizar":"Guardar"}</Btn>
            <Btn variant="default" onClick={()=>{setForm(false);resetForm();}}>Cancelar</Btn>
          </div>
        </Card>
      )}

      <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Buscar cliente o N°..." className="mb-3" />
      <Card><Table cols={columnas} rows={filas} /></Card>
      
      {!esAdmin && filtrados.length === 0 && <Card className="text-center py-8"><p className="text-muted">Módulo exclusivo de Administración.</p></Card>}
    </div>
  );
}